import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { todayLocal } from "@/lib/bookingDates";
import {
  countRequiredOpenChargeRuleSlots,
  type ChargeRuleCoverage,
} from "@/lib/chargeRuleCoverage";
import { useBookingRealtimeRefresh } from "@/lib/useBookingRealtimeRefresh";
import type { Booking, BookingDeparture } from "@/types/camping";

export interface NavBadges {
  arrivalsToday: number;
  departuresToday: number;
  inquiriesOpen: number;
  chargeRulesRequiredOpen: number;
  ticketsOpen: number;
}

function countOpenArrivals(bookings: Booking[]): number {
  return bookings.filter((b) =>
    ["confirmed", "reserved", "requested"].includes(b.status),
  ).length;
}

function countOpenDepartures(rows: BookingDeparture[]): number {
  return rows.filter((b) => b.status === "checked_in").length;
}

export function useNavBadges(enabled: boolean): NavBadges {
  const [badges, setBadges] = useState<NavBadges>({
    arrivalsToday: 0,
    departuresToday: 0,
    inquiriesOpen: 0,
    chargeRulesRequiredOpen: 0,
    ticketsOpen: 0,
  });

  const refresh = useCallback(() => {
    if (!enabled) return;
    const today = todayLocal();
    void Promise.allSettled([
      api.get<Booking[]>(`/api/v1/bookings/arrivals?arrival_date=${today}`),
      api.get<BookingDeparture[]>(`/api/v1/bookings/departures?departure_date=${today}`),
      api.get<{ count: number }>("/api/v1/bookings/inquiries/count"),
      api.get<ChargeRuleCoverage>("/api/v1/charge-rules/coverage"),
      api.get<{ actionable: number }>("/api/v1/tickets/summary"),
    ]).then(([arrivalsResult, departuresResult, inquiriesResult, coverageResult, ticketsResult]) => {
      setBadges((prev) => ({
        arrivalsToday:
          arrivalsResult.status === "fulfilled"
            ? countOpenArrivals(arrivalsResult.value)
            : prev.arrivalsToday,
        departuresToday:
          departuresResult.status === "fulfilled"
            ? countOpenDepartures(departuresResult.value)
            : prev.departuresToday,
        inquiriesOpen:
          inquiriesResult.status === "fulfilled"
            ? inquiriesResult.value.count
            : prev.inquiriesOpen,
        chargeRulesRequiredOpen:
          coverageResult.status === "fulfilled"
            ? countRequiredOpenChargeRuleSlots(coverageResult.value.slots)
            : prev.chargeRulesRequiredOpen,
        ticketsOpen:
          ticketsResult.status === "fulfilled" ? ticketsResult.value.actionable : prev.ticketsOpen,
      }));
    });
  }, [enabled]);

  useBookingRealtimeRefresh(refresh, { enabled });

  useEffect(() => {
    if (!enabled) return;
    let intervalId: number | undefined;
    const startPolling = () => {
      refresh();
      intervalId = window.setInterval(refresh, 120_000);
    };
    // Badge-API erst nach erstem Paint – entlastet Navigation auf Mobilgeräten
    if (typeof window.requestIdleCallback === "function") {
      const idleId = window.requestIdleCallback(() => startPolling(), { timeout: 2500 });
      return () => {
        window.cancelIdleCallback(idleId);
        if (intervalId !== undefined) window.clearInterval(intervalId);
      };
    }
    const timeoutId = window.setTimeout(startPolling, 400);
    return () => {
      window.clearTimeout(timeoutId);
      if (intervalId !== undefined) window.clearInterval(intervalId);
    };
  }, [enabled, refresh]);

  return badges;
}
